import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { Badge } from './ui/badge';
import { getDomains, getProgress, updateProgress, generateExplanation } from '../api';
import { RotateCcw, ChevronLeft, ChevronRight, Check, X, Brain, Home, Loader2 } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from '../hooks/use-toast';

const FlashcardMode = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const domainParam = searchParams.get('domain');
  const taskParam = searchParams.get('task');

  const [loading, setLoading] = useState(true);
  const [cards, setCards] = useState([]);
  const [domains, setDomains] = useState([]);
  const [progress, setProgress] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [reviewedCards, setReviewedCards] = useState([]);
  const [sessionProgress, setSessionProgress] = useState({ easy: 0, hard: 0 });
  const [explanations, setExplanations] = useState({});
  const [loadingExplanation, setLoadingExplanation] = useState(false);

  useEffect(() => {
    fetchData();
  }, [domainParam, taskParam]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [domainsData, progressData] = await Promise.all([
        getDomains(),
        getProgress()
      ]);
      
      setDomains(domainsData);
      setProgress(progressData);
      
      // Build cards list based on params
      let allCards = [];
      
      if (taskParam) {
        // Study specific task
        domainsData.forEach(domain => {
          const task = domain.items.find(item => item.code === taskParam);
          if (task) allCards.push(task);
        });
      } else if (domainParam) {
        // Study all tasks in domain
        const domain = domainsData.find(d => d.id === domainParam);
        if (domain) allCards = domain.items;
      } else {
        // Study all due for review
        domainsData.forEach(domain => {
          domain.items.forEach(item => {
            const itemProgress = progressData.item_progress[item.code];
            if (itemProgress && itemProgress.next_review && new Date(itemProgress.next_review) <= new Date()) {
              allCards.push(item);
            }
          });
        });
        
        // If no cards due, show all with low mastery
        if (allCards.length === 0) {
          domainsData.forEach(domain => {
            domain.items.forEach(item => {
              const itemProgress = progressData.item_progress[item.code];
              if (!itemProgress || itemProgress.mastery < 80) {
                allCards.push(item);
              }
            });
          });
        }
      }
      
      setCards(allCards);
    } catch (error) {
      console.error('Error fetching flashcard data:', error);
      toast({
        title: 'Error loading flashcards',
        description: 'Failed to load flashcards. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const currentCard = cards[currentIndex];
  const domain = domains.find(d => d.id === currentCard?.domain);
  const cardProgress = currentCard && progress ? progress.item_progress[currentCard.code] : null;

  const handleFlip = async () => {
    if (!isFlipped && currentCard && !explanations[currentCard.code]) {
      // Generate explanation when flipping for the first time
      setLoadingExplanation(true);
      try {
        const result = await generateExplanation(currentCard.code, currentCard.description);
        setExplanations(prev => ({
          ...prev,
          [currentCard.code]: result.explanation_html || result.explanation
        }));
      } catch (error) {
        console.error('Error generating explanation:', error);
        toast({
          title: 'Could not load explanation',
          description: 'Showing basic information instead.',
          variant: 'destructive'
        });
      } finally {
        setLoadingExplanation(false);
      }
    }
    setIsFlipped(!isFlipped);
  };

  const handleRating = async (difficulty) => {
    if (!currentCard) return;

    try {
      // Update progress in backend
      const result = await updateProgress(currentCard.code, difficulty);
      
      setReviewedCards([...reviewedCards, { ...currentCard, rating: difficulty, newMastery: result.new_mastery }]);
      setSessionProgress(prev => ({
        ...prev,
        [difficulty]: prev[difficulty] + 1
      }));

      toast({
        title: difficulty === 'easy' ? 'Great!' : 'Keep practicing!',
        description: `Mastery updated to ${result.new_mastery}%`,
      });

      // Move to next card
      if (currentIndex < cards.length - 1) {
        setCurrentIndex(currentIndex + 1);
        setIsFlipped(false);
      } else {
        // Session complete
        toast({
          title: 'Session Complete!',
          description: `Reviewed ${cards.length} cards`,
        });
      }
    } catch (error) {
      console.error('Error updating progress:', error);
      toast({
        title: 'Error',
        description: 'Failed to update progress. Please try again.',
        variant: 'destructive'
      });
    }
  };

  const handleNext = () => {
    if (currentIndex < cards.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setIsFlipped(false);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setIsFlipped(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-blue-600" />
      </div>
    );
  }

  if (cards.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle>No Cards Available</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-slate-600">No flashcards are currently due for review.</p>
            <Button onClick={() => navigate('/browse')} className="w-full">
              Browse Task List
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const sessionComplete = currentIndex >= cards.length - 1 && reviewedCards.length === cards.length;

  if (sessionComplete) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 pb-20">
        <div className="max-w-4xl mx-auto p-6 space-y-6 mb-20">
          <Card className="border-t-4 border-t-green-500">
            <CardHeader>
              <CardTitle className="text-3xl flex items-center gap-2">
                <Check className="h-8 w-8 text-green-600" />
                Session Complete!
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-6 bg-green-50 rounded-lg">
                  <div className="text-4xl font-bold text-green-600">{sessionProgress.easy}</div>
                  <div className="text-slate-600 mt-2">Easy Cards</div>
                </div>
                <div className="text-center p-6 bg-orange-50 rounded-lg">
                  <div className="text-4xl font-bold text-orange-600">{sessionProgress.hard}</div>
                  <div className="text-slate-600 mt-2">Hard Cards</div>
                </div>
              </div>
              
              <div className="space-y-2">
                <Button onClick={() => navigate('/')} className="w-full" size="lg">
                  <Home className="mr-2 h-5 w-5" />
                  Back to Dashboard
                </Button>
                <Button 
                  onClick={() => window.location.reload()} 
                  variant="outline" 
                  className="w-full" 
                  size="lg"
                >
                  <RotateCcw className="mr-2 h-5 w-5" />
                  Review Again
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 pb-20">
      <div className="max-w-4xl mx-auto p-6 space-y-6 mb-20">{/* Added mb-20 for bottom margin */}
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Flashcard Review</h1>
            <p className="text-slate-600 mt-1">
              Card {currentIndex + 1} of {cards.length}
            </p>
          </div>
          <Button onClick={() => navigate('/')} variant="outline">
            <Home className="mr-2 h-4 w-4" />
            Home
          </Button>
        </div>

        {/* Progress Bar */}
        <div className="space-y-2">
          <Progress value={((currentIndex + 1) / cards.length) * 100} className="h-3" />
          <div className="flex justify-between text-sm text-slate-500">
            <span>Session Progress</span>
            <span>{Math.round(((currentIndex + 1) / cards.length) * 100)}%</span>
          </div>
        </div>

        {/* Flashcard */}
        <Card 
          className="min-h-[400px] cursor-pointer hover:shadow-2xl transition-all duration-300 border-t-4"
          style={{ borderTopColor: domain?.color }}
          onClick={handleFlip}
        >
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Badge 
                  variant="outline" 
                  className="text-lg font-bold px-3 py-1"
                  style={{ borderColor: domain?.color, color: domain?.color }}
                >
                  {currentCard?.code}
                </Badge>
                <span className="text-sm text-slate-600">{domain?.name}</span>
              </div>
              <Badge className="bg-slate-100 text-slate-700">
                {currentCard?.difficulty}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="flex items-center justify-center min-h-[300px]">
            <div className="text-center space-y-4 px-6">
              {!isFlipped ? (
                <>
                  <Brain className="h-12 w-12 mx-auto text-slate-400" />
                  <p className="text-2xl font-medium leading-relaxed text-slate-700">
                    {currentCard?.description}
                  </p>
                  <p className="text-sm text-slate-500 mt-4">Click to see explanation</p>
                </>
              ) : (
                <div className="space-y-4 w-full">
                  {loadingExplanation ? (
                    <div className="flex flex-col items-center justify-center py-8">
                      <Loader2 className="h-8 w-8 animate-spin text-blue-600 mb-3" />
                      <p className="text-sm text-slate-600">Generating AI explanation...</p>
                    </div>
                  ) : (
                    <>
                      <div 
                        className="text-left space-y-3 bg-blue-50 p-6 rounded-lg flashcard-explanation"
                        dangerouslySetInnerHTML={{ 
                          __html: explanations[currentCard?.code] || `<p>This is a ${currentCard?.difficulty} level task in the ${domain?.name} domain. Understanding this concept is important for the BACB exam.</p>`
                        }}
                      />
                      {cardProgress && (
                        <div className="mt-4 p-3 bg-white rounded border border-blue-200">
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-slate-600">Current Mastery:</span>
                            <span className="text-lg font-bold" style={{ color: domain?.color }}>
                              {cardProgress.mastery}%
                            </span>
                          </div>
                          <Progress value={cardProgress.mastery} className="h-2 mt-2" />
                        </div>
                      )}
                      <p className="text-sm text-slate-500 mt-4">Rate your understanding below</p>
                    </>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Controls */}
        <div className="flex items-center gap-4 pb-8">{/* Added pb-8 for extra bottom padding */}
          <Button 
            onClick={handlePrevious} 
            disabled={currentIndex === 0}
            variant="outline"
            size="lg"
            className="flex-1"
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
          
          {isFlipped && (
            <>
              <Button 
                onClick={() => handleRating('hard')} 
                variant="outline"
                size="lg"
                className="flex-1 border-orange-300 hover:bg-orange-50 hover:border-orange-400 transition-all duration-200"
              >
                <X className="mr-2 h-5 w-5 text-orange-600" />
                Hard
              </Button>
              <Button 
                onClick={() => handleRating('easy')} 
                size="lg"
                className="flex-1 bg-green-600 hover:bg-green-700 transition-all duration-200"
              >
                <Check className="mr-2 h-5 w-5" />
                Easy
              </Button>
            </>
          )}
          
          {!isFlipped && (
            <Button 
              onClick={handleFlip} 
              size="lg"
              className="flex-2"
              style={{ backgroundColor: domain?.color }}
            >
              <RotateCcw className="mr-2 h-5 w-5" />
              Flip Card
            </Button>
          )}
          
          <Button 
            onClick={handleNext} 
            disabled={currentIndex >= cards.length - 1}
            variant="outline"
            size="lg"
            className="flex-1"
          >
            <ChevronRight className="h-5 w-5" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FlashcardMode;